import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTrash } from 'react-icons/fa';
import useOutsideClick from '../hooks/useOutsideClick';

function VasanItems({ allVasanItems, VasanRows, setVasanRows, isExpanded, setIsExpanded }) {
  const { t } = useTranslation();
  const [isViewVasanItemsList, setIsViewVasanItemsList] = useState(false);
  const [isPopover, setIsPopover] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef(null);

  const unitTypeList = [
    { key: 'Pieces', name: 'નંગ (Pieces)' },
    { key: 'Per_Person', name: 'માણસો દીઠ (Per Person)' }
  ];

  React.useEffect(() => {
    if (isPopover !== null && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [isPopover]);

  const popoverRef = useRef(null);

  useOutsideClick(popoverRef, () => {
    setIsPopover(null);
  });

  const showVasanItemsList = () => {
    setIsViewVasanItemsList(!isViewVasanItemsList);
    setIsExpanded(!isExpanded);
  };

  const togglePopover = (index) => {
    setIsPopover(isPopover === index ? null : index);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleFarsanClick = (index, vasan) => {
    setVasanRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { ...row, vasan, unitType: vasan.unitType } : row
      )
    );
    setIsPopover(null);
    setSearchInput('');
  };

  const addNewRow = () => {
    setVasanRows([...VasanRows, { vasan: null, quantity: '' }]);
    setSearchInput('');
  };

  const removeRow = (index) => {
    setVasanRows((prevRows) => prevRows.filter((_, i) => i !== index));
    setSearchInput('');
  };

  const clearRow = (index) => {
    setVasanRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { vasan: null, unitType: '', quantity: '' } : row
      )
    );
    setSearchInput('');
  };

  const isLastRowFilled = () => {
    const lastRow = VasanRows[VasanRows.length - 1];
    return lastRow.vasan && lastRow.unitType.trim() && lastRow.quantity.trim();
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setVasanRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { ...row, [name]: value } : row
      )
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100">
      {/* Header */}
      <div
        className={`bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 cursor-pointer ${isViewVasanItemsList ? 'rounded-t-2xl' : 'rounded-2xl'}`}
        onClick={(e) => {
          e.stopPropagation();
          showVasanItemsList();
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{t('VasanItems')}</h3>
              <p className="text-purple-100 text-sm">{t('add_vasan_ingredients')}</p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              showVasanItemsList();
            }}
            className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200"
          >
            <svg
              className={`w-5 h-5 text-white transform transition-transform duration-200 ${isViewVasanItemsList ? 'rotate-180' : ''
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isViewVasanItemsList && (
        <div className="p-6">
          <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
            {VasanRows.map((row, index) => (
              <div key={index}>
                <div className="grid grid-cols-4 gap-4">
                  {/* Vasan Selection */}
                  <div className="relative" ref={popoverRef}>
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('VasanItems')}
                      </label>
                    )}
                    <div
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchInput('');
                        togglePopover(index);
                        setIsExpanded(true);
                      }}
                    >
                      <span className={row.vasan ? 'text-gray-900' : 'text-gray-500'}>
                        {row.vasan ? row.vasan.name : t('pls_sel_vasanitems')}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {isPopover === index && (
                      <div
                        className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto z-[9999] w-full"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3 border-b border-gray-200">
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search vasan items..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm"
                            value={searchInput}
                            onChange={handleSearchChange}
                          />
                        </div>
                        <div className="max-h-36 overflow-auto">
                          {allVasanItems
                            ?.filter((vasan) =>
                              vasan.name.toLowerCase().includes(searchInput.toLowerCase().trim())
                            )
                            .map((vasan) => (
                              <div
                                key={vasan._id}
                                className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                onClick={() => handleFarsanClick(index, vasan)}
                              >
                                {vasan.name}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Unit Type */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('unit_type')}
                      </label>
                    )}
                    <select
                      name="unitType"
                      value={row.unitType}
                      onChange={(e) => handleChange(index, e)}
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 appearance-none cursor-pointer"
                    >
                      <option value="">{t('select_options')}</option>
                      {unitTypeList.map((unit) => (
                        <option key={unit.key} value={unit.key}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('Quantity')}
                      </label>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      placeholder={t('Quantity')}
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50"
                      value={row.quantity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const cleaned = raw
                          .replace(/[^0-9.]/g, '')
                          .replace(/(\..*)\./g, '$1');
                        setVasanRows((prevRows) =>
                          prevRows.map((r, i) =>
                            i === index ? { ...r, quantity: cleaned } : r
                          )
                        );
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-end justify-start">
                    {VasanRows.length - 1 === index ? (
                      <div className="flex gap-2">
                        <button
                          className="px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={addNewRow}
                          disabled={!isLastRowFilled()}
                        >
                          <FaPlus className="w-4 h-4 mr-2" />
                          {t('btnAddNew')}
                        </button>
                        <button
                          className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
                          onClick={() => clearRow(index)}
                        >
                          {t('btnReset')}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center justify-center"
                        onClick={() => removeRow(index)}
                      >
                        <FaTrash className="w-4 h-4 mr-2" />
                        {t('btnDelete')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default VasanItems;
